import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Dialog,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Avatar,
  Chip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { RootState } from "../../../store/Index";
import apiService from "../../../service/Api";
import {
  FieldLabel,
  StyledTextField,
} from "../../companies/types/company-form";

interface LinkToCompanyDialogProps {
  open: boolean;
  onClose: () => void;
  contactId: number;
  currentCompanyBranch?: {
    id: number;
    name: string;
    company: { id: number; name: string; company_logo?: string | null };
  } | null;
  onSuccess: () => void;
}

interface CompanySearchResult {
  id: number;
  name: string;
  company_logo?: string | null;
  city?: string | null;
}

interface BranchResult {
  id: number;
  name: string;
  city?: string | null;
}

function debounce<T extends (...args: string[]) => void>(fn: T, delay: number) {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

export default function LinkToCompanyDialog({
  open,
  onClose,
  contactId,
  currentCompanyBranch,
  onSuccess,
}: LinkToCompanyDialogProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const color100 = reduxTheme.primary_colors?.[100];
  const color300 = reduxTheme.primary_colors?.[300];
  const color400 = reduxTheme.primary_colors?.[400] ?? "#8B4513";
  const color700 = reduxTheme.primary_colors?.[700];
  const color800 = reduxTheme.primary_colors?.[800];
  const color900 = reduxTheme.primary_colors?.[900];

  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [companyResults, setCompanyResults] = useState<CompanySearchResult[]>(
    [],
  );

  const [selectedCompany, setSelectedCompany] =
    useState<CompanySearchResult | null>(null);
  const [branches, setBranches] = useState<BranchResult[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);

  const [submitting, setSubmitting] = useState(false);

  const debouncedSearch = useRef(
    debounce(async (query: string) => {
      if (!query.trim()) {
        setCompanyResults([]);
        return;
      }
      setSearching(true);
      try {
        const res = await apiService.get<{ results: CompanySearchResult[] }>(
          `api/companies/company/?search=${encodeURIComponent(query)}`,
        );
        setCompanyResults(res.data.results ?? []);
      } catch {
        setCompanyResults([]);
      } finally {
        setSearching(false);
      }
    }, 500),
  ).current;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    debouncedSearch(e.target.value);
  };

  const handleSelectCompany = (company: CompanySearchResult) => {
    setSelectedCompany(company);
    setSearchQuery("");
    setCompanyResults([]);
    setSelectedBranchId(null);
    setBranchesLoading(true);
    apiService
      .get<{ results: BranchResult[] }>(
        `api/companies/company-branch/?company=${company.id}`,
      )
      .then((res) => {
        const results = res.data.results ?? [];
        setBranches(results);
        if (results.length === 1) setSelectedBranchId(results[0].id);
      })
      .catch(() => setBranches([]))
      .finally(() => setBranchesLoading(false));
  };

  const handleBackToSearch = () => {
    setSelectedCompany(null);
    setBranches([]);
    setSelectedBranchId(null);
  };

  const handleClose = () => {
    setSearchQuery("");
    setCompanyResults([]);
    setSelectedCompany(null);
    setBranches([]);
    setSelectedBranchId(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!selectedBranchId) return;
    setSubmitting(true);
    try {
      await apiService.patch(`api/contacts/contact/${contactId}/`, {
        company_branch_id: selectedBranchId,
      });
      toast.success("Contact linked to company successfully!");
      onSuccess();
      handleClose();
    } catch {
      toast.error("Failed to link contact. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // reset internal state whenever dialog is reopened
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setCompanyResults([]);
      setSelectedCompany(null);
      setBranches([]);
      setSelectedBranchId(null);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: color900,
          borderRadius: "20px",
          display: "flex",
          flexDirection: "column",
          maxHeight: "85vh",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: `linear-gradient(135deg, ${color400} 0%, #c9834a 100%)`,
          borderRadius: "20px 20px 0 0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {selectedCompany && (
            <IconButton
              size="small"
              onClick={handleBackToSearch}
              sx={{ color: "#fff" }}
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          <BusinessIcon sx={{ color: "#fff", fontSize: 20 }} />
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{ color: "#fff" }}
          >
            Link to Company
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: "#fff" }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 3 }}>
        {currentCompanyBranch && !selectedCompany && (
          <Box
            sx={{
              mb: 2.5,
              p: 1.25,
              borderRadius: "12px",
              border: "1px dashed",
              borderColor: alpha(color300 ?? "#ccc", 0.3),
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: color300 }}>
              Currently linked to{" "}
              <strong style={{ color: color100 }}>
                {currentCompanyBranch.company.name}
              </strong>{" "}
              · {currentCompanyBranch.name}
            </Typography>
          </Box>
        )}

        {/* Step 1: search companies */}
        {!selectedCompany && (
          <>
            <FieldLabel textcolor={color100}>Search for a company</FieldLabel>
            <StyledTextField
              placeholder="Search companies..."
              value={searchQuery}
              onChange={handleSearchChange}
              inputbg={color800}
              textcolor={color700}
              placeholdercolor={color300}
              InputProps={{
                startAdornment: searching ? (
                  <CircularProgress size={14} sx={{ mr: 1 }} />
                ) : (
                  <SearchIcon
                    sx={{ fontSize: 16, color: "text.disabled", mr: 1 }}
                  />
                ),
              }}
            />

            {companyResults.length > 0 && (
              <Box
                sx={{
                  mt: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                }}
              >
                {companyResults.map((company) => (
                  <Box
                    key={company.id}
                    onClick={() => handleSelectCompany(company)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.25,
                      borderRadius: "12px",
                      cursor: "pointer",
                      border: "1px solid",
                      borderColor: alpha(color300 ?? "#ccc", 0.2),
                      "&:hover": { bgcolor: alpha(color400 ?? "#ccc", 0.06) },
                    }}
                  >
                    <Avatar
                      src={company.company_logo ?? undefined}
                      sx={{ width: 32, height: 32, borderRadius: 1 }}
                    >
                      <BusinessIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {company.name}
                      </Typography>
                      {company.city && (
                        <Typography variant="caption" color="text.secondary">
                          {company.city}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {searchQuery.trim() &&
              !searching &&
              companyResults.length === 0 && (
                <Typography
                  variant="caption"
                  sx={{ color: color300, mt: 1, display: "block" }}
                >
                  No companies found.
                </Typography>
              )}
          </>
        )}

        {/* Step 2: pick branch */}
        {selectedCompany && (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                borderRadius: "12px",
                border: "1px solid",
                borderColor: alpha(color400 ?? "#ccc", 0.3),
                bgcolor: alpha(color400 ?? "#ccc", 0.04),
                mb: 2.5,
              }}
            >
              <Avatar
                src={selectedCompany.company_logo ?? undefined}
                sx={{ width: 32, height: 32, borderRadius: 1 }}
              >
                <BusinessIcon sx={{ fontSize: 16 }} />
              </Avatar>
              <Typography variant="body2" fontWeight={600}>
                {selectedCompany.name}
              </Typography>
            </Box>

            <FieldLabel textcolor={color100}>
              Select Branch{" "}
              {branchesLoading && <CircularProgress size={10} sx={{ ml: 1 }} />}
            </FieldLabel>
            {!branchesLoading && branches.length === 0 && (
              <Typography variant="caption" sx={{ color: color300 }}>
                No branches found for this company.
              </Typography>
            )}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.75 }}>
              {branches.map((branch) => (
                <Chip
                  key={branch.id}
                  label={branch.name + (branch.city ? ` · ${branch.city}` : "")}
                  onClick={() =>
                    setSelectedBranchId((prev) =>
                      prev === branch.id ? null : branch.id,
                    )
                  }
                  variant={
                    selectedBranchId === branch.id ? "filled" : "outlined"
                  }
                  sx={{
                    borderColor: alpha(color400 ?? "#ccc", 0.4),
                    backgroundColor:
                      selectedBranchId === branch.id ? color400 : "transparent",
                    color: selectedBranchId === branch.id ? "#fff" : color300,
                    fontWeight: 600,
                    fontSize: "12px",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor:
                        selectedBranchId === branch.id
                          ? color400
                          : alpha(color400 ?? "#ccc", 0.08),
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Footer */}
      {selectedCompany && (
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: color900,
            borderRadius: "0 0 20px 20px",
          }}
        >
          <Button
            onClick={handleClose}
            disabled={submitting}
            sx={{ textTransform: "none", color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={submitting || !selectedBranchId}
            startIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 4,
              bgcolor: color400,
              "&:hover": { bgcolor: color400 },
              "&.Mui-disabled": {
                bgcolor: alpha(color400 ?? "#ccc", 0.4),
                color: "#fff",
              },
            }}
          >
            {submitting ? "Linking..." : "Confirm Link"}
          </Button>
        </Box>
      )}
    </Dialog>
  );
}
